// 模块 12（AI 智能体）的界面文案。放在模块内，不改共享的 i18n/zh.ts（并行开发时避免冲突）。

export const t = {
  nav: "AI 智能体",
  navModels: "AI 模型",
  adminTitle: "AI 智能体",
  adminSub: "配置智能体：用哪个模型、系统提示词与用户提示词、技能、交互与预算；保存即生成新版本",
  modelsTitle: "AI 模型",
  modelsSub: "接入模型供应商（DeepSeek、火山方舟、Claude、OpenAI……），管理模型目录、按用途的默认模型与机构 AI 设置",
  navMemory: "AI 记忆",
  navMap: "AI 地图",
  mapPageTitle: "AI 地图",
  mapPageNoRobot: "没有能看的机器人",
  mapPageSub: "会话地图与场地地图：地点、走过的路、标签锚点与位姿图约束、每个地点看到的画面",
  placeFrames: "看画面",
  placeFramesHide: "收起",
  placeFramesEmpty: "这个地点没有画面（图片过了保留期，或没有查看画面的权限）",
  profiles: "智能体",
  addProfile: "新建智能体",
  name: "名称",
  // 供应商
  providersTitle: "供应商",
  addProvider: "添加供应商",
  preset: "预设",
  presetCustom: "自建（OpenAI 兼容）",
  protocol: "协议",
  protocols: {
    anthropic_messages: "Anthropic Messages",
    openai_responses: "OpenAI Responses",
    openai_chat: "OpenAI 兼容 Chat",
    doubao_realtime_v3: "豆包实时语音",
  } as Record<string, string>,
  baseUrl: "接口地址",
  baseUrlHint: "选预设时留空用官方地址；自建必填，必须是 https（校内自建的 http 地址需平台管理员放行）",
  apiKey: "API 密钥",
  apiKeyHint: "加密保存，之后只显示后 4 位",
  keyLast4: "密钥",
  residency: "数据去向",
  residencies: { cn: "境内", overseas: "境外", private: "私有化" } as Record<string, string>,
  residencyHint: "只作标记，显示供应商在境内还是境外，不影响模型选择",
  enabled: "启用",
  disabled: "停用",
  test: "测试",
  testResult: (r: { ok: boolean | null; detail?: string; models_listed?: number | null } | null) =>
    !r ? "未测试" : r.ok === true ? `连通${r.models_listed != null ? `（${r.models_listed} 个模型）` : ""}` : r.ok === null ? "不支持列模型，请用模型探针" : `失败：${r.detail ?? ""}`,
  deleteProviderConfirm: (n: string) => `删除供应商"${n}"？名下还有模型时不能删除，可以停用。`,
  // 模型
  modelsCard: "模型",
  addModel: "添加模型",
  fromPreset: "从预设添加",
  manual: "手工添加",
  modelId: "模型 ID",
  modelIdHint: "发给供应商的值；火山方舟可以填接入点 ep-…",
  likePreset: "能力与参数等同于预设",
  likePresetNone: "（不套用，能力未知，需跑探针）",
  displayName: "显示名",
  caps: { tools: "工具", vision: "看图", thinking: "思考", context: "上下文", price: "价格" },
  tri: { yes: "是", no: "否", unknown: "未知" } as Record<string, string>,
  unpriced: "未标定",
  presetUpdate: "预设有更新",
  migrated: "迁移而来，请测试",
  probe: { connect: "连通", tools: "工具", vision: "看图" } as Record<string, string>,
  probeHint: "探针会调用模型，每次产生少量费用；每个模型每分钟最多 3 次",
  archive: "归档",
  enable: "启用",
  disable: "停用",
  // 默认与设置
  defaultsTitle: "按用途的默认模型",
  defaultsHint: "智能体没指定模型或指定的不可用时，按顺序取第一个可用的",
  purposes: { dialogue: "对话（主循环，要能调用工具）", vision: "看图（对话模型不能看图时由它描述画面）", dialogue_fast: "快速对话（语音流水线）", realtime_voice: "实时语音", embedding: "向量化（记忆与知识库，要 1024 维）", perception: "关键帧理解（空间记忆，要能看图，便宜快的模型）" } as Record<string, string>,
  addToChain: "加入",
  settingsTitle: "机构 AI 设置",
  allowVision: "允许智能体看图",
  requirePricing: "只用已标定价格的模型",
  realtimeFor: "实时语音开放范围",
  realtimeOptions: { off: "不开放（尚在实测）", teachers: "只给教师", all: "所有能用智能体的人" } as Record<string, string>,
  rejected: {
    model_missing: "模型不存在",
    model_disabled: "模型已停用",
    provider_disabled: "供应商已停用",
    kind_mismatch: "模型种类不对",
    "capability_gap:tools": "不能调用工具（或未测试）",
    "capability_gap:vision": "不能看图（或未测试）",
    "modality_gap:image": "不能看图",
    unpriced: "价格未标定",
    "capability_gap:dimensions": "向量维度对不上（要 1024 维）",
    not_needed: "（前面已选中）",
  } as Record<string, string>,
  // 智能体
  model: "模型",
  followDefault: (name?: string) => `跟随机构默认${name ? `（当前：${name}）` : ""}`,
  resolvedTo: "会用到",
  noModel: "没有可用的模型",
  noVisionModel: "未配置：对话模型能看图时直接看；不能看图时看图技能不可用",
  noPerceptionModel: "未配置：看到的画面不做结构化理解（不进物体表）",
  robot: "绑定机器人",
  anyRobot: "不绑定（机构内通用）",
  systemPrompt: "系统提示词",
  systemPromptHint: "角色、领域、规则、说话格式。平台底线（安全与数据规则）总在前面，不能覆盖；平台默认（口语化、简短）可以被这里的要求覆盖",
  systemPromptTemplate: "## 角色\n你叫小元，是……\n\n## 领域\n\n## 规则\n\n## 说话格式\n",
  // 系统提示词样例（开发计划 17 AI-02）：四个小标题与模板一致；都只描述说话、表情、原地动作能做的事
  systemPromptSample: "套用样例",
  systemPromptSampleHint: "会替换掉系统提示词，再按需要改",
  systemPromptSampleConfirm: "用样例替换掉现在的系统提示词？",
  systemPromptSamples: {
    greeter: {
      label: "迎宾",
      text:
        "## 角色\n你叫小元，是实验楼门口的迎宾机器人，负责欢迎来访的师生和访客，回答关于这栋楼的常见问题。\n\n## 领域\n这栋楼有哪些实验室和办公室、开放时间、哪位老师负责（不说联系方式）、今天的活动安排（以发起人给你的信息为准，不知道就说不知道）。\n\n## 规则\n- 有人靠近先问好，再问需要什么帮助。\n- 你站在原地，不能带路、不能移动；别人问路时口头说明方向，或建议找前台。\n- 不知道的事直接说不知道，不要猜；涉及个人信息的问题不回答。\n- 遇到紧急情况（有人受伤、火警）提醒对方立刻找工作人员或拨打紧急电话。\n\n## 说话格式\n热情、礼貌、简短，每次一两句；对老人和小孩多说一遍、说得更简单；可以用表情配合问候。\n",
    },
    assistant: {
      label: "课堂助教",
      text:
        "## 角色\n你叫小元，是这门课的课堂助教机器人，配合老师完成课堂互动：提问、复述要点、带领讨论、回答同学的问题。\n\n## 领域\n本节课的内容以老师（发起人）告诉你的为准；超出课程范围的问题可以简单回答，但要说明不是本课内容。\n\n## 规则\n- 老师的指令优先；同学的要求如果和老师说的冲突，以老师为准。\n- 不替同学做作业、不直接给考试答案，可以给思路和提示。\n- 不点评具体同学的表现，不记录任何人的个人信息。\n- 你不能走动；需要走到某处时请老师或同学帮忙。\n\n## 说话格式\n清楚、有条理，每次一两句；提问后留出时间等同学回答；用表情配合鼓励和肯定。\n",
    },
    qa: {
      label: "答疑",
      text:
        "## 角色\n你叫小元，是学习答疑机器人，帮同学解答课程相关的问题，给出思路和参考方向。\n\n## 领域\n课程知识点、概念解释、解题思路、学习方法。不涉及成绩、评教和任何人的隐私。\n\n## 规则\n- 先确认问题，再分步讲解；一次只讲一个要点，讲完问对方是否听懂。\n- 不确定的内容如实说不确定，建议对方查教材或问老师，不要编造出处或数据。\n- 你不能上网、不能查文件、不能移动，也记不住某个人的情况；有人要求这些时说明做不到。\n- 被要求做与学习无关的事（比如开玩笑式地指挥你做危险动作）就礼貌拒绝。\n\n## 说话格式\n耐心、平和、口语化，每次一两句，多用例子，少用术语。\n",
    },
  } as Record<string, { label: string; text: string }>,
  userPrompt: "用户提示词（首条消息模板）",
  userPromptHint: "套在任务外面的固定背景，例如\"你在课堂上给大一新生讲解，任务：{{task}}\"。只能用下面的变量；没写 {{task}} 时任务接在末尾",
  insertVar: "插入变量",
  vars: { task: "任务", robot_name: "机器人名称", starter_role: "发起人角色（老师、管理员…）", date: "日期", time: "时间", weekday: "星期" } as Record<string, string>,
  defaultTask: "开场任务",
  defaultTaskHint: "开始时没写任务就用它，例如\"跟进来的同学打个招呼\"",
  preview: "预览实际生效的提示词",
  previewRobot: "按哪台机器人生成技能清单",
  previewTitle: "实际生效的提示词",
  previewSystem: "系统提示",
  previewFirst: "首条消息",
  previewTokens: (n: number) => `约 ${n} token（估算）`,
  knowledge: "知识库",
  knowledgeSoon: "即将支持：绑定课件、讲义，按资料回答（依赖语音与知识库模块 C3-06）",
  vision: "看图",
  imageMaxSide: "图像长边上限（像素）",
  imageMaxSideHint: "默认 640（个人信息最小化）；要看清白板文字可调到 1024",
  safetyProfile: "安全档位",
  safetyProfiles: { general: "常规", strict: "严格（更严的输出过滤）" } as Record<string, string>,
  note: "版本说明",
  notePlaceholder: "这次改了什么（可留空）",
  versions: "版本历史",
  rollback: "回滚到这一版",
  rollbackConfirm: (v: number) => `把 v${v} 的内容复制成一个新版本？之后新开的会话用它，正在运行的不受影响。`,
  conflict: "别人刚改过这个智能体，请关闭后重新打开再改（你的修改不会保存）",
  conflictTitle: (at: string) => `别人${at ? `在 ${at} ` : "刚"}改过这个智能体。下面是当前版本与你的表单不同的地方：`,
  conflictNoDiff: "内容没有不同（只是版本号变了）",
  conflictField: "字段",
  conflictMine: "你的",
  conflictTheirs: "当前版本",
  conflictKeepMine: "以我的为准保存",
  conflictTakeTheirs: "改用当前版本（丢弃我的修改）",
  archiveModelConfirm: (n: string) => `归档模型"${n}"？归档后不能再被选用，历史记录保留。`,
  current: "当前",
  autonomy: "自主级别",
  autonomyLevels: ["L0 只说话与查状态", "L1 加表情灯光与原地动作", "L2 加移动（有界移动，转向要现场批准）"],
  autonomyHint: "L2 的移动要机器人策略把避障闸设为截停（enforce）；教学模式下最高 L1",
  approvalsTitle: "等待批准",
  approvalKinds: { turn: "转向", back: "后退一小步", posture: "姿态操作", macro: "走到某处" } as Record<string, string>,
  approvalKindExplore: "探索",
  approvalGoTo: (to: string, m: number, legs: number, turns: number) =>
    `走到${to}（约 ${m} m、${legs} 个路点，途中最多转向 ${turns} 次，转向不再逐次确认）`,
  /** 模型看画面判断的方向（相对开始时的朝向，左正） */
  approvalToward: (deg: number) =>
    Math.abs(deg) < 10 ? "；朝正前方" : `；朝${deg > 0 ? "左" : "右"}前方约 ${Math.round(Math.abs(deg))}°`,
  approvalExplore: (hint: string | null, maxM: number, minutes: number, turns: number) =>
    `探索周围${hint ? `（找「${hint}」）` : ""}：最多走 ${maxM} m、${minutes} 分钟，其中转向不超过 ${turns} 次`,
  approvalTurn: (deg: number) => `原地${deg > 0 ? "向左" : "向右"}转 ${Math.abs(deg)}°`,
  approvalBack: (m: number) => `后退 ${m} m（身后没有传感器：请确认身后没有人和东西）`,
  approvalReason: "智能体给的理由",
  approvalOnSite: "我在现场，确认机器人周围没有人和障碍",
  approvalApprove: "批准",
  approvalReject: "拒绝",
  approvalExpires: (t: string) => `${t} 前有效`,
  skills: "技能",
  skillNames: {
    say: "说话",
    get_status: "查看状态",
    show_emotion: "表情",
    set_light: "灯效",
    play_gesture: "原地动作（挥手、打招呼……）",
    plan: "计划（多步骤任务先写下计划）",
    remember: "记一笔（跨会话保留，机构共享）",
    recall: "回想（以前的经历与笔记）",
    observe: "看（调俯仰、看相机画面；要开看图）",
    where_is: "找东西（空间记忆：看到的画面存成关键帧、进物体表；会话里记地图与轨迹）",
    where_am_i: "看自己在哪（要勾「找东西」）",
    name_place: "给地点、区域起名（要勾「找东西」）",
    map_overview: "看地图概览：按区域折叠（要勾「找东西」）",
    expand: "展开地图里的一个区域（要勾「找东西」）",
    verify: "看画面确认一件事（如\"门开着吗\"；要开看图）",
    get_surroundings: "查周围的障碍（8 个方向）",
    move: "走一小段（L2；前后 0.2~1 m，只有前方有障碍传感器）",
    turn: "原地转向（L2；每次都要现场的人批准）",
    back_up: "后退一小步（L2；0.2~0.3 m，身后没有传感器，每次都要现场的人批准）",
    posture: "姿态操作（变形、趴下、起立；要现场的人批准，机器人策略要允许）",
    go_to: "走到记下的地方（连续交互、要勾「找东西」；整体批准一次）",
    explore: "探索周围没走过的方向，记下新地点（连续交互、要勾「找东西」；整体批准一次，预算以米、分钟计）",
    cancel: "取消进行中的 go_to / explore",
  } as Record<string, string>,
  budgets: "预算与安全",
  interaction: "交互方式",
  mode: "会话方式",
  modeTask: "完成任务就结束",
  modeInteractive: "连续交互（说完等待现场的人或事件）",
  idleMinutes: "空闲多少分钟后结束",
  maxTurnsPerReply: "每次回应最多几轮模型调用",
  effort: "推理强度",
  effortDefault: "供应商默认",
  effortOff: "关闭思考（最快）",
  effortLow: "低（最快，适合现场对话）",
  effortMedium: "中",
  effortHigh: "高（慢，适合复杂任务）",
  callTimeout: "单次模型调用截止（秒）",
  heartbeat: "闭环心跳（秒，0 = 关）",
  heartbeatHint: "空闲时每隔这么久唤醒一次，按计划决定下一步；不推迟空闲结束，电量低于 20% 时停用。可设 30~600",
  keyframeS: "自动关键帧间隔（秒，0 = 关）",
  keyframeSHint: "勾了「找东西」时，静止时每隔这么久取一张（转身、调俯仰时另取）；画面几乎没变的不留。可设 30~600",
  maxKeyframes: "每小时最多几张关键帧",
  maxDeliberations: "每小时最多被唤醒几次",
  maxDeliberationsHint: "超出后只降频（心跳停、现场事件延后），不结束会话；发起人的话照常回应",
  maxSteps: "每会话模型调用次数上限",
  maxMinutes: "每会话时长上限（分钟）",
  maxTokens: "每会话 token 上限（0 = 不限）",
  maxCallsPerMin: "每分钟技能调用上限",
  maxOutputTokens: "每次回复 token 上限",
  disclosure: "开场提示语（可留空；填了机器人会先说这句）",
  blockedTerms: "拦截词（每行一个，说话内容含有即拦截）",
  version: "版本",
  deleteProfileConfirm: (n: string) => `归档智能体"${n}"？归档后不能再开始会话，记录与版本保留。`,
  runTitle: "AI 智能体",
  runSub: "给智能体下任务：它会用有限的技能完成，过程可见，随时可停",
  profile: "智能体",
  task: "任务",
  taskPlaceholder: "例如：先介绍一下你自己，再跟大家挥手打招呼",
  start: "开始",
  stop: "停止智能体",
  send: "补充",
  followupPlaceholder: "给智能体补充一句（会在当前动作完成后送达）",
  running: "运行中",
  exportJson: "导出 JSON",
  budgetLeft: "剩余预算",
  budgetSteps: (used: number, max: number) => `步数 ${used}/${max}`,
  budgetTokens: (used: number, max: number) =>
    max === 0 ? `token ${used.toLocaleString("zh-CN")}（不限）` : `token ${used.toLocaleString("zh-CN")}/${max.toLocaleString("zh-CN")}`,
  budgetTime: (s: number) => `时间 ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`,
  aiControlling: (holder: string) => `${holder || "AI 智能体"}正在控制这台机器人`,
  openRunPage: "打开运行页",
  paused: "已暂停",
  resume: "继续",
  pausedHint: (why: string) => `${why}，移动类技能已暂停；确认现场安全、机器人重新归平台控制后点“继续”，否则到时会话自动结束`,
  // 暂停原因 → 文案；{who} 是接管方
  pauseReasons: {
    control_lost: "机器人被{who}接管",
    control_owner_changed: "机器人的控制权到了{who}那里",
    mode_changed: "机器人的形态被别人切换了",
    fallen: "机器人跌倒了",
    lifted: "机器人被提起了",
    held: "机器人被牵住了",
    edge_takeover: "机器人被{who}接管（端侧已暂停肢体与移动）",
    edge_low_battery: "机器人电量低（端侧已暂停肢体与移动）",
    edge_fallen: "机器人跌倒了（端侧已暂停肢体与移动）",
    edge_lifted: "机器人被提起了（端侧已暂停肢体与移动）",
    edge_held: "机器人被牵住了（端侧已暂停肢体与移动）",
    edge_paused: "机器人端侧暂停了肢体与移动",
    edge_still_paused: "继续之后机器人端侧仍在暂停（比如遥控器还占着、机器人还被提着），放开后再点继续",
  } as Record<string, string>,
  pauseUnknown: "机器人需要确认",
  // 模块 13 自主模式
  approvalMode: "批准方式",
  approvalModes: { manual: "每次确认", auto_move: "自动批准移动", auto_all: "自动批准移动和趴下起立" } as Record<string, string>,
  approvalModeHint: {
    manual: "探索、转向、姿态等动作每次都要在这里批准",
    auto_move: "探索、走到某处、转向、后退不再弹卡片；姿态仍要批准。只对这一次会话生效，都记入审计",
    auto_all: "移动和趴下、起立不再弹卡片；变形、从卸力状态起身仍要批准。只对这一次会话生效，都记入审计",
  } as Record<string, string>,
  autoConfirm: "我确认现场有人看着机器人，能随时急停或接管",
  autoApprovalOn: (mode: string) => `批准方式：${mode}（动作不再弹卡片，急停和接管随时可用）`,
  runMode: "运行方式",
  runModes: { cloud: "控制台会话", onboard_console: "自主模式 · 控制台监督", onboard_on_site: "自主模式 · 现场监护" } as Record<string, string>,
  runModeHint: "自主模式：现场的人都按访客对待；厂商语音要先切到 only_voice。现场监护最长 2 小时，不需要控制台一直开着",
  supervisor: "现场监护人",
  supervisorPlaceholder: "在机器人旁边、带着遥控器的人",
  rcConfirm: "我确认现场监护人带着遥控器，能随时接管",
  onboardTitle: "端侧（自主）",
  supervisions: { console: "控制台监督", on_site: "现场监护", unattended: "无人值守" } as Record<string, string>,
  onboardStates: { none: "无票据", online: "在线", paused: "已暂停", offline: "离线（宽限中）", suspended: "重连中", stopped: "已停止", unknown: "未知" } as Record<string, string>,
  edgeDisconnected: "未连接（机器人离线或数据过期）",
  voiceModeWarn: (m: string) => `厂商语音现在是 ${m}，自主模式要先在远程运维里切到 only_voice`,
  edgeState: "端侧状态",
  grant: "票据",
  grantLeft: (n: number, s: number) => `第 ${n} 张 · 剩 ${s} 秒`,
  grantExpired: (n: number) => `第 ${n} 张 · 已到期`,
  grantRevoked: (r: string) => `已撤销（${r}）`,
  allowText: (a: { gesture?: boolean; locomotion?: boolean }) => (a.locomotion ? "允许说话、动作、移动" : a.gesture ? "允许说话、动作" : "只允许说话、表情"),
  edgeEvents: "端侧事件",
  noEdgeEvents: "还没有端侧事件",
  eventKinds: { trigger: "触发", state: "状态", local_action: "本地动作", gap: "缺口", unknown: "未知" } as Record<string, string>,
  gapText: (n: number) => `断网期间丢了 ${n} 条事件`,
  onboardSection: "自主模式",
  onboardSectionHint: "只在自主模式会话里用。规则在机器人本地执行（不经过大模型），事件照样交给智能体",
  duty: "职责说明",
  dutyHint: "会加在自主模式会话的提示词里，比如「你是物理系的讲解员，只介绍展品」",
  onlineRules: "在线时的规则",
  offlineRules: "断网时的反应（只能是表情或灯效）",
  ruleOn: "触发",
  ruleAction: "本地动作",
  triggers: { touch: "被触摸", wake: "听到唤醒词" } as Record<string, string>,
  rulesHint: "本地动作现在只能是表情或灯效：触摸的长按是静音并停止、厂商唤醒时会转身，确认之前不开放预设动作；冷却 3~3600 秒",
  localActions: { emotion: "表情", led: "灯效", preset: "预设动作" } as Record<string, string>,
  actionParam: { emotion: "表情 id（逗号分隔）", led: "灯效模式号", preset: "预设动作 id" } as Record<string, string>,
  cooldown: "冷却（秒，至少 3）",
  seconds: "秒",
  addRule: "添加规则",
  goals: "常驻目标",
  goalEvery: "每",
  goalMinutes: "间隔（分钟）",
  goalMinutesUnit: "分钟",
  goalText: "要做的事",
  goalPlaceholder: "比如：巡查一遍实验室，看看门窗关没关",
  goalsHint: "只在自主模式、连续交互（空闲分钟数 > 0）的会话里按时提醒智能体，第一次在开始后一个间隔；间隔 5~240 分钟且不超过会话时长上限，最多 5 个。有常驻目标的会话不因空闲结束（开了心跳会一直跑到时长上限）。授权不允许移动时只做原地能做的",
  addGoal: "添加常驻目标",
  removeGoal: "删除常驻目标",
  removeRule: "删除规则",
  configState: "配置",
  configApplied: (v: number) => `v${v} 已生效`,
  configPending: (v: number) => `v${v} 待确认`,
  configRejected: (v: number, e: string) => `v${v} 被机器人拒收：${e}`,
  pauseOwners: { rc: "遥控器", app_proxy: "手机 App", app_proxy_t_mode: "手机 App", interaction: "厂商语音", pnc: "厂商跟随/导航" } as Record<string, string>,
  ended: "已结束",
  endReason: "结束原因",
  noProfile: "还没有可用的智能体，请先到「AI 智能体」页面新建",
  taskOptional: (d: string) => `可留空：留空就执行开场任务"${d}"`,
  noModelError: "智能体没有可用的模型（首选模型不可用，机构默认里也没有能调用工具的模型）",
  history: "最近的会话",
  steps: "过程",
  noSteps: "无权查看步骤详情",
  plan: "计划",
  planStatus: { todo: "待做", doing: "进行中", done: "完成", failed: "失败", skipped: "跳过" } as Record<string, string>,
  // 记忆页
  memoryTitle: "AI 记忆",
  memorySub: "智能体跨会话的记忆：经历（程序记下的事件流水）与笔记（智能体或人记下的事实）。他人发起的任务原文只显示摘要",
  memoryRobot: "机器人",
  memoryAllRobots: "全部机器人（笔记按机构共享）",
  memorySearch: "搜索（按意思找，也按文字匹配）",
  memoryScopes: { all: "全部", episodes: "经历", notes: "笔记" } as Record<string, string>,
  memoryKinds: {
    task: "任务",
    heard: "发起人的话",
    said: "机器人说",
    skill: "动作",
    event: "现场",
    summary: "会话摘要",
    agent: "智能体记的",
    user: "人写的",
    consolidation: "整理生成",
  } as Record<string, string>,
  memoryRedacted: "（他人发起，只显示摘要）",
  memoryExpires: (at: string) => `到 ${at} 过期`,
  memoryEmpty: "还没有记忆",
  addNote: "写一条笔记",
  notePlaceholder2: "例如：302 实验室的门禁卡在王老师那里（≤ 200 字，只写事实）",
  noteSave: "保存笔记",
  memoryDelete: "删除",
  memoryDeleteConfirm: "删除这条记忆？",
  memoryClear: "清空这台机器人的记忆",
  memoryClearConfirm: (n: string) => `清空"${n}"的全部记忆（经历、在它上面记下的笔记、关键帧与图片、物体、会话地图与轨迹）？不能恢复。`,
  memoryCleared: (e: number, n: number, k = 0, o = 0, m = 0) => `已删除 ${e} 条经历、${n} 条笔记、${k} 张关键帧、${o} 个物体、${m} 张地图`,
  objectsTitle: "看到过的东西",
  objectsHint: "智能体看到的物体（同一件东西多次看到合并成一条）。置信度随时间下降，可移动的东西降得快",
  objectsEmpty: "还没有看到过东西（要勾「找东西」并让智能体看一看）",
  objectStatus: { present: "在", uncertain: "不确定", gone: "已移走" } as Record<string, string>,
  movability: { low: "很少挪动", medium: "偶尔挪动", high: "经常挪动" } as Record<string, string>,
  markGone: "标为已移走",
  markPresent: "还在",
  seenTimes: (n: number) => `看到 ${n} 次`,
  liveMapTitle: "实时地图",
  liveMapHint: "机器人现在所在的会话图：浅绿是看过的空地，深色是有东西的地方，红色是 Wi-Fi 信号弱的地方，箭头是机器人。每 2 秒刷新",
  liveMapWifi: (dbm: number) => `Wi-Fi ${dbm} dBm`,
  liveMapWeakCell: (dbm: number, lost: boolean) =>
    lost ? `Wi-Fi 约 ${Math.round(dbm)} dBm，走到这儿时断过线` : `Wi-Fi 约 ${Math.round(dbm)} dBm`,
  liveMapEmpty: "还没有地图：开了「找东西」的会话跑起来以后会边走边建",
  liveMapUnlocated: "机器人现在不在这张图里（边缘重启或换了形态），显示的是最近一张",
  liveMapSigma: (m: number) => `位置误差约 ${m.toFixed(1)} m`,
  mapTitle: "会话地图",
  mapHint: "开了空间记忆的会话会记下走过的路与地点（以会话开始时的位置为原点，正前方朝右）。里程计断开过的那段不画",
  mapEmpty: "还没有会话地图（要勾「找东西」并跑一次会话）",
  mapPick: "会话",
  mapPlaces: (n: number) => `${n} 个地点`,
  mapNoPlaces: "这次会话还没有记下地点",
  placeLabel: (no: number, name: string | null) => (name ? `${no} · ${name}` : `地点 ${no}`),
  placeVisits: (n: number) => `来过 ${n} 次`,
  placeName: "地点名",
  placeRename: "改名",
  placeNameSave: "保存",
  placeObjects: "这里看到过",
  placeSummary: "摘要",
  retentionTitle: "保留期",
  retentionHint: "每晚维护按这里删除超期的图片与记录；缩短后，超期的内容在下一次维护时删除。图片与轨迹不能长于记录",
  retentionImage: "关键帧图片（天，1~365）",
  retentionRecord: "经历、关键帧记录、物体（天，30~1095）",
  retentionTrack: "走过的轨迹（天，1~365）",
  retentionSave: "保存保留期",
  retentionInvalid: "天数要是整数且在范围内，图片与轨迹不能长于记录",
  retentionSaved: "保留期已保存",
  epochLabel: { session_start: "会话开始", form_carry: "形态切换后沿用", unlocated: "里程计断开（未定位）", anchor: "标签定位" } as Record<string, string>,
  siteMap: (name: string | null, isDefault: boolean) => `场地地图 · ${name ?? "未命名"}${isDefault ? "（默认）" : ""}`,
  siteMapNew: "新建场地地图",
  siteMapName: "场地地图名（如：三楼）",
  siteMapCreate: "建图",
  siteMapCreated: "场地地图已建好",
  siteMapHint:
    "场地地图跨会话保留（一层楼一张）。在墙上贴 tag36h11 标签（边长 ≥ 20 cm），在这里登记位置与正面朝向；机器人看到登记过的标签就在这张图里定位。" +
    "第一次定位要走近一个标签（约 1.3 m 内、相机接近平视），或同时看到两个标签",
  siteMapDefault: "设为默认",
  siteMapIsDefault: "默认场地地图",
  siteMapRename: "改名",
  regionsTitle: "区域",
  regionsHint: "程序按位置把地点分成区域（一间屋、一段走廊），每晚重算；智能体看地图概览时只看到区域，再展开相关的。人起的名智能体不能改",
  regionLabel: (no: number, name: string | null) => (name ? `${name}（区域 ${no}）` : `区域 ${no}`),
  regionPlaces: (n: number) => `${n} 个地点`,
  regionName: "区域名",
  regionNamedByAgent: "（智能体起的名）",
  siteMapDelete: "删除这张图",
  siteMapDeleteConfirm: (name: string | null) =>
    `删除场地地图「${name ?? "未命名"}」？地点、标签锚点、定位记录和挂在这些地点上的智能体笔记会一起删掉（关键帧与人写的笔记留下），不能恢复`,
  siteMapDeleted: "场地地图已删除",
  siteMapRenameCancel: "取消",
  siteMapUnsetDefault: "取消默认",
  siteMapUnsetDefaultConfirm: "取消默认场地地图？之后不再做标签定位，会话改回用会话地图",
  anchorsTitle: "标签锚点",
  anchorsEmpty: "还没有登记标签",
  anchorTag: "标签号",
  anchorSize: "边长（cm）",
  anchorX: "x（m）",
  anchorY: "y（m）",
  anchorZ: "离地高（m，可不填）",
  anchorYaw: "正面朝向（°）",
  anchorLabel: "备注",
  anchorAdd: "登记",
  anchorDelete: "删除",
  anchorDeleteConfirm: "删除这个标签锚点？",
  anchorLine: (a: { tag_id: number; size_m: number; x: number; y: number; z: number | null; yaw: number; label: string | null }) =>
    `标签 ${a.tag_id}${a.label ? `（${a.label}）` : ""} · (${a.x.toFixed(2)}, ${a.y.toFixed(2)}) m · 朝 ${Math.round((a.yaw * 180) / Math.PI)}° · ${Math.round(a.size_m * 100)} cm${a.z != null ? ` · 离地 ${a.z.toFixed(2)} m` : ""}`,
  obsTitle: "看到的标签（约束）",
  obsFilter: "显示",
  obsFilters: { all: "全部", outliers: "只看离群", revoked: "只看已撤销" } as Record<string, string>,
  obsNoneMatching: "没有符合条件的观测",
  obsMore: "更早的",
  obsOptimizePending: "已撤销；重新优化没成功，稍后由后台补上",
  obsEmpty: "还没看到登记过的标签",
  obsReason: { jump: "跳变，待确认" } as Record<string, string>,
  obsLine: (o: { tag_id: number; dist_m: number; x: number; y: number; yaw: number }) =>
    `标签 ${o.tag_id} · ${o.dist_m.toFixed(1)} m 外 · 推出机器人在 (${o.x.toFixed(2)}, ${o.y.toFixed(2)}) 朝 ${Math.round((o.yaw * 180) / Math.PI)}°`,
  obsAccepted: "已采用",
  relocalize: "现在定位一次",
  relocalizeHint: "取一张画面，马上检测标签并定位（机器人不动；正在看视频时相机忙，先关掉视频）",
  relocalizeResult: (r: { seen: number[]; accepted: boolean; reason: string | null; location: { located: boolean; x?: number; y?: number; heading_deg?: number } }) => {
    const seen = r.seen.length ? `看到标签 ${r.seen.join("、")}` : "没看到登记过的标签";
    const why: Record<string, string> = {
      no_pose: "没有位姿（趴下时里程计停发）",
      moving: "机器人在动",
      no_pitch: "相机不平视或不知道俯仰（四足舵机 0°、双足 90° 才用）",
      no_image: "没有画面",
      no_site_map: "没有默认场地地图",
      no_anchors: "场地地图里还没登记标签",
      no_heading: "定不了朝向：走近一个标签或同时看到两个",
      jump: "和已有定位差太多，等下一次确认",
      stale: "这帧比已有定位旧",
    };
    const loc = r.location.located
      ? `现在在 (${r.location.x?.toFixed(2)}, ${r.location.y?.toFixed(2)}) 朝 ${Math.round(r.location.heading_deg ?? 0)}°`
      : "还没定位";
    return `${seen}；${r.accepted ? "已定位" : (why[r.reason ?? ""] ?? "没有采用")}；${loc}`;
  },
  obsInlier: "内点",
  obsOutlier: (m: number) => `离群（差 ${m.toFixed(2)} m）`,
  obsRevoked: "已撤销",
  obsRevoke: "撤销",
  obsRestore: "恢复",
  obsHeading: { pair: "两标签定向", normal: "近处定向", known: "沿用朝向" } as Record<string, string>,
  graphStats: (s: { nodes: number; constraints: number; outliers: number; unlocated: number }, at: string) =>
    `位姿图：${s.nodes} 个节点、${s.constraints} 条约束、${s.outliers} 条离群${s.unlocated ? `、${s.unlocated} 段改回未定位` : ""} · ${at}`,
  graphHint: "约束就是每次看到标签的观测：离群的不参与定位；撤销后不再参与，并立即重新优化",
  epochConfidence: (c: string) => `定位置信度：${c}`,
  confidence: "置信度",
  banner: (who: string) => `AI 控制中 · ${who} 发起`,
  kinds: { user: "发起人", model: "模型", tool: "技能", observation: "现场/总结/心跳", error: "错误", config: "配置" } as Record<string, string>,
  reasons: {
    finished: "完成",
    stopped_by_user: "被停止",
    starter_offline: "发起人离开（控制台心跳中断）",
    on_site_time_limit: "现场监护达到 2 小时上限",
    onboard_unsupported: "机器人不再支持端侧票据",
    onboard_revoked: "端侧票据已被撤销",
    onboard_stopped: "机器人端侧终止了票据（撤销或急停）",
    onboard_capability_changed: "机器人重连后端侧票据能力变了（换了边缘版本）",
    lease_ai_agent_preempted: "有人申请了控制",
    lease_takeover: "被抢占",
    lease_estop: "急停",
    lease_expired: "租约过期",
    lease_lost: "失去控制权",
    paused_timeout: "暂停后未继续",
    robot_offline: "机器人离线",
    budget_time: "达到时长上限",
    budget_tokens: "达到 token 上限",
    budget_steps: "达到调用次数上限",
    model_refused: "模型拒绝",
    content_filtered: "内容被拦截",
    permission_revoked: "发起人权限变化",
    server_restart: "服务重启",
    lease_unavailable: "机器人正被他人控制",
    model_error: "模型调用出错",
    idle_timeout: "空闲超时",
    context_overflow: "对话太长，超出了模型能处理的长度",
    quota_exceeded: "达到机构的 AI 用量上限",
    "model_error:insufficient_quota": "模型账户余额或额度已用尽",
    "model_error:context_overflow": "对话太长，超出了模型能处理的长度",
    stopped: "已停止",
    "model_error:timeout": "模型调用超时",
    "model_error:network": "连不上模型服务",
    "model_error:auth": "模型密钥无效或无权限",
    "model_error:rate_limited": "模型服务限流",
    "model_error:http_4xx": "模型服务拒绝了请求",
    "model_error:http_5xx": "模型服务出错",
    "model_error:redirect": "模型地址发生了重定向（已拒绝）",
    "model_error:bad_response": "模型返回格式异常",
    model_stopped: "模型提前停止",
    max_output_tokens: "回复被输出上限截断",
    internal_error: "内部错误",
  } as Record<string, string>,
  tokens: "token",
  cost: "花费",
  unpricedPart: (n: number) => `另有 ${n} 次调用未标定价格`,
  // 用量页（AX-08 + MC-07）
  navUsage: "AI 用量",
  usageTitle: "AI 用量",
  usageSub: "每次模型调用的用量与花费：按日期、用户、模型、用途或机器人汇总；不同币种分开合计，没标定价格的模型显示“未标定”",
  usageSelfOnly: "只显示你自己发起的调用（看全机构要「AI 模型」管理权限）",
  usageFrom: "从",
  usageTo: "到",
  usageGroup: "汇总方式",
  usageGroups: { day: "按日期", user: "按用户", model: "按模型", purpose: "按用途", robot: "按机器人" } as Record<string, string>,
  usagePurpose: "用途",
  usagePurposes: {
    dialogue: "对话",
    vision: "看图委托",
    perception: "画面理解",
    verify: "核对地点",
    probe: "连通测试",
    embedding: "向量化",
  } as Record<string, string>,
  usageAll: "全部",
  usageTotal: (c: string) => `合计（${c}）`,
  usageUnpricedTotal: "未标定价格",
  usageCalls: "调用",
  usageErrors: "失败",
  usageInput: "输入",
  usageCached: "其中缓存",
  usageOutput: "输出",
  usageCost: "花费",
  usageKey: "分组",
  usageUnknown: "（无）",
  usageDetail: "调用明细",
  usageTime: "时间",
  usageWho: "发起人",
  usageModel: "模型",
  usageStatus: "结果",
  usageLatency: "耗时",
  usageMore: "加载更多",
  usagePeak: "高峰价",
  usageStatuses: { ok: "成功", error: "失败", cancelled: "已取消" } as Record<string, string>,
  usageBadRange: "请选择开始和结束日期：开始不能晚于结束，最长 366 天",
  usageTruncated: (n: number) => `分组太多，只列出前 ${n} 行（合计包含全部）`,
  usagePickHint: "点一行只看这个用户或模型的明细",
  usagePicked: (name: string) => `只看：${name}`,
  usageClearPick: "看全部",
} as const;

export function reasonText(r: string | null | undefined): string {
  if (!r) return "—";
  // "model_stopped: pause_turn" 之类带细节的原因按前缀取文案
  return t.reasons[r] ?? t.reasons[r.split(":")[0] ?? r] ?? r;
}
